import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Cpu,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  Settings,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import InstallPwaButton from "@/components/InstallPwaButton";

const GOLD = "#E8B339";

/* ----------------------------- Animated Meda Eye ----------------------------- */
function MedaEye({ phase }) {
  const opening = phase === "opening";
  return (
    <div
      className="relative mx-auto aspect-[5/3] w-72 sm:w-80 lg:w-[28rem]"
      data-testid="meda-eye"
    >
      {/* Orbital rings */}
      <div className="pointer-events-none absolute inset-0">
        <div className="meda-spin absolute inset-0" style={{ animationDuration: "16s" }}>
          <div className="meda-ellipse" style={{ transform: "scaleY(0.34) rotate(-18deg)" }} />
        </div>
        <div className="meda-spin-rev absolute inset-0" style={{ animationDuration: "24s" }}>
          <div className="meda-ellipse" style={{ transform: "scaleY(0.28) rotate(14deg)", opacity: 0.5 }} />
        </div>
      </div>

      {/* Twinkling particles */}
      {[
        { t: "12%", l: "18%", d: "0s" },
        { t: "20%", l: "82%", d: "0.6s" },
        { t: "78%", l: "26%", d: "1.1s" },
        { t: "70%", l: "74%", d: "0.3s" },
        { t: "46%", l: "6%", d: "0.9s" },
        { t: "50%", l: "94%", d: "1.4s" },
      ].map((p, i) => (
        <span
          key={i}
          className="meda-twinkle absolute h-1 w-1 rounded-full bg-amber-300"
          style={{ top: p.t, left: p.l, animationDelay: p.d, boxShadow: `0 0 6px ${GOLD}` }}
        />
      ))}

      {/* The eye (blinks open on login) */}
      <svg
        viewBox="0 0 400 240"
        className={`relative h-full w-full ${opening ? "meda-blink" : ""}`}
        style={{ transformOrigin: "200px 120px" }}
      >
        <defs>
          <radialGradient id="iris" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor="#9CC6FF" />
            <stop offset="38%" stopColor="#2F7DF6" />
            <stop offset="72%" stopColor="#1437A6" />
            <stop offset="100%" stopColor="#06122E" />
          </radialGradient>
          <radialGradient id="irisGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="goldStroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FCE7A1" />
            <stop offset="50%" stopColor={GOLD} />
            <stop offset="100%" stopColor="#9A6B12" />
          </linearGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* layered almond lids */}
        <g filter="url(#glow)" fill="none" stroke="url(#goldStroke)">
          <path d="M40,120 Q200,18 360,120 Q200,222 40,120 Z" strokeWidth="3.5" />
          <path d="M70,120 Q200,46 330,120 Q200,194 70,120 Z" strokeWidth="2" opacity="0.7" />
          <path d="M96,120 Q200,66 304,120 Q200,174 96,120 Z" strokeWidth="1.2" opacity="0.45" />
        </g>

        {/* iris */}
        <g className={opening ? "meda-flare" : "meda-pulse"} style={{ transformOrigin: "200px 120px" }}>
          <circle cx="200" cy="120" r="78" fill="url(#irisGlow)" />
          <circle cx="200" cy="120" r="54" fill="url(#iris)" />
          {/* concentric gold rings */}
          <circle cx="200" cy="120" r="58" fill="none" stroke="url(#goldStroke)" strokeWidth="1.4" opacity="0.85" />
          <circle cx="200" cy="120" r="64" fill="none" stroke="url(#goldStroke)" strokeWidth="0.8" opacity="0.5" />
          {/* pupil */}
          <circle cx="200" cy="120" r={opening ? 14 : 24} fill="#020611" className="meda-pupil" />
          <circle cx="188" cy="106" r="6" fill="#fff" opacity="0.85" />
        </g>
      </svg>
    </div>
  );
}

const FEATURES = [
  { icon: Cpu, label: "AI-Powered\nDecisions" },
  { icon: TrendingUp, label: "Real-time\nMonitoring" },
  { icon: Settings, label: "End-to-End\nAutomation" },
];

function FeaturePills({ className = "" }) {
  return (
    <div className={`flex items-start justify-center gap-6 sm:gap-10 ${className}`} data-testid="login-features">
      {FEATURES.map((f) => (
        <div key={f.label} className="flex w-24 flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full border border-amber-400/30 bg-white/[0.03]">
            <f.icon className="h-6 w-6 text-amber-400" />
          </div>
          <span className="mt-2 whitespace-pre-line text-xs leading-tight text-slate-300">
            {f.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/* --------------------------------- Page --------------------------------- */
export default function AdminLoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | submitting | opening
  const timer = useRef(null);

  useEffect(() => {
    if (user) nav(user.role === "super_admin" ? "/super" : "/admin", { replace: true });
  }, [user, nav]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (phase !== "idle") return;
    setPhase("submitting");
    try {
      const u = await login(email, password);
      // Eye blinks open + flares — "entering the CRM"
      setPhase("opening");
      timer.current = setTimeout(() => {
        nav(u.role === "super_admin" ? "/super" : "/admin");
      }, 950);
    } catch (err) {
      toast.error(formatApiError(err));
      setPhase("idle");
    }
  };

  const busy = phase !== "idle";

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#060B16] text-white" data-testid="login-page">
      <MedaStyles />

      {/* ambient glows */}
      <div className="pointer-events-none absolute -left-40 top-10 h-96 w-96 rounded-full bg-amber-500/10 blur-[120px]" />
      <div className="pointer-events-none absolute -right-32 top-1/3 h-96 w-96 rounded-full bg-blue-600/10 blur-[120px]" />
      {/* bottom gold shimmer */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 opacity-70">
        <svg viewBox="0 0 1440 200" className="h-full w-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="wave" x1="0" x2="1">
              <stop offset="0%" stopColor={GOLD} stopOpacity="0" />
              <stop offset="50%" stopColor={GOLD} stopOpacity="0.55" />
              <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0,140 C320,40 680,200 1440,90 L1440,200 L0,200 Z" fill="url(#wave)" opacity="0.35" />
          <path d="M0,160 C400,80 900,210 1440,120" fill="none" stroke="url(#wave)" strokeWidth="2" />
        </svg>
      </div>

      <Link
        to="/"
        data-testid="login-home"
        className="absolute left-6 top-6 z-10 font-display text-sm font-bold tracking-tight text-white/80 hover:text-white"
        aria-label="Back to home"
      >
        <span className="text-amber-400">&larr;</span> Home
      </Link>

      <div className="relative z-10 mx-auto grid min-h-screen max-w-6xl items-center gap-6 px-6 py-8 lg:grid-cols-2 lg:gap-12 lg:py-10">
        {/* Brand / Eye */}
        <section className="meda-rise flex flex-col items-center">
          <MedaEye phase={phase} />
          <h2 className="mt-6 hidden text-center font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:block lg:text-5xl">
            The Future of
            <br />
            <span className="text-amber-400">Admissions</span>{" "}
            <span className="text-white">Meet</span>{" "}
            <span className="text-amber-400">Meda Eye</span>
          </h2>
          <div className="my-5 h-px w-40 bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-amber-400/90">
            Your AI Team Leader
          </p>
          <FeaturePills className="mt-10 hidden lg:flex" />
        </section>

        {/* Login card */}
        <section className="flex justify-center">
          <form
            onSubmit={onSubmit}
            data-testid="admin-login-form"
            className="meda-rise w-full max-w-md rounded-3xl border border-amber-400/25 bg-white/[0.035] p-7 shadow-[0_0_60px_-15px_rgba(232,179,57,0.25)] backdrop-blur-xl sm:p-9"
            style={{ animationDelay: "0.1s" }}
          >
            <h1 className="text-center font-display text-4xl font-bold tracking-tight">
              Meda <span className="text-amber-400">AI</span>
            </h1>
            <p className="mt-2 text-center text-lg font-semibold text-amber-400">
              Welcome Back!
            </p>
            <p className="mt-1 text-center text-sm text-slate-400">
              Login to continue to your account
            </p>

            <div className="mt-8 space-y-4">
              {/* Email / Phone */}
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 transition-colors focus-within:border-amber-400/60">
                <Mail className="h-5 w-5 shrink-0 text-amber-400" />
                <input
                  type="text"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email or Phone"
                  autoComplete="username"
                  data-testid="login-email"
                  className="h-14 w-full bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
                />
              </div>

              {/* Password */}
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 transition-colors focus-within:border-amber-400/60">
                <Lock className="h-5 w-5 shrink-0 text-amber-400" />
                <input
                  type={showPwd ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoComplete="current-password"
                  data-testid="login-password"
                  className="h-14 w-full bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="shrink-0 text-slate-400 hover:text-amber-400"
                  data-testid="login-toggle-password"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                >
                  {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              <div className="text-right">
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-link"
                  className="text-sm font-medium text-amber-400 hover:underline"
                >
                  Forgot Password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="mt-7 flex h-14 w-full items-center justify-center rounded-xl bg-gradient-to-r from-[#F6D277] via-[#E8B339] to-[#C9971F] text-base font-bold text-[#1A1206] shadow-[0_10px_30px_-8px_rgba(232,179,57,0.6)] transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-90"
            >
              {phase === "submitting" ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Authenticating…
                </>
              ) : phase === "opening" ? (
                "Entering…"
              ) : (
                "Login"
              )}
            </button>

            <p className="mt-6 text-center text-xs text-slate-500">
              Having trouble? Contact{" "}
              <span className="text-amber-400/90">hello@medadmits.com</span>
            </p>

            <div className="mt-4 flex justify-center">
              <InstallPwaButton
                label="Install Meda AI app"
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 px-4 py-2 text-xs font-semibold text-amber-400 transition-colors hover:bg-amber-400/10"
              />
            </div>
          </form>
        </section>

        {/* Mobile feature pills */}
        <FeaturePills className="lg:hidden" />
      </div>

      {/* Open / flare overlay — fills the screen as the eye "opens" */}
      {phase === "opening" && (
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center" data-testid="login-enter-overlay">
          <div className="meda-flash h-24 w-24 rounded-full" />
        </div>
      )}
    </div>
  );
}

/* keyframes (scoped, self-contained) */
function MedaStyles() {
  return (
    <style>{`
      @keyframes meda-spin { to { transform: rotate(360deg); } }
      @keyframes meda-spin-rev { to { transform: rotate(-360deg); } }
      .meda-spin { animation: meda-spin linear infinite; }
      .meda-spin-rev { animation: meda-spin-rev linear infinite; }
      .meda-ellipse {
        position: absolute; inset: 6%;
        border: 1px solid rgba(232,179,57,0.55);
        border-radius: 50%;
        box-shadow: 0 0 12px rgba(232,179,57,0.25);
      }
      @keyframes meda-twinkle { 0%,100%{opacity:.15; transform:scale(.7);} 50%{opacity:1; transform:scale(1.2);} }
      .meda-twinkle { animation: meda-twinkle 2.6s ease-in-out infinite; }
      @keyframes meda-pulse { 0%,100%{ transform: scale(1); opacity:.92;} 50%{ transform: scale(1.04); opacity:1;} }
      .meda-pulse { animation: meda-pulse 3.4s ease-in-out infinite; }
      @keyframes meda-blink { 0%{transform:scaleY(1);} 40%{transform:scaleY(.06);} 60%{transform:scaleY(.06);} 100%{transform:scaleY(1);} }
      .meda-blink { animation: meda-blink .42s cubic-bezier(.4,0,.2,1) both; }
      @keyframes meda-flare { 0%{transform:scale(1); filter:brightness(1);} 100%{transform:scale(1.18); filter:brightness(1.9);} }
      .meda-flare { animation: meda-flare .9s ease-out forwards; }
      .meda-pupil { transition: r .4s ease; }
      @keyframes meda-flash { 0%{transform:scale(0); opacity:0; box-shadow:0 0 60px 30px rgba(232,179,57,.9);} 20%{opacity:1;} 100%{transform:scale(40); opacity:1; box-shadow:0 0 120px 80px rgba(255,255,255,.95);} }
      .meda-flash { background: radial-gradient(circle, #fff 0%, #FCE7A1 30%, #E8B339 60%, rgba(232,179,57,0) 75%); animation: meda-flash .95s cubic-bezier(.5,0,.5,1) forwards; }
      @keyframes meda-rise { 0%{opacity:0; transform:translateY(16px);} 100%{opacity:1; transform:translateY(0);} }
      .meda-rise { animation: meda-rise .7s ease both; }
      @media (prefers-reduced-motion: reduce) {
        .meda-spin,.meda-spin-rev,.meda-twinkle,.meda-pulse,.meda-blink,.meda-flare,.meda-rise { animation: none !important; }
      }
    `}</style>
  );
}
